import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {AppStore} from '@iv/store/app.store';
import * as Redux from 'redux';
import {AppState} from '@iv/store/app.reducer';
import {getCurrentTab, getTabs} from '@iv/store/tabs/tabs.selectors';
import {getAllInstruments} from '@iv/store/instruments/instrument.reducer';
import {Future, Instrument} from '@iv/model/instrument';
import {Tab, TabTypeEnum} from '@iv/model/tabs.model';
import * as TabsActions from '../../../store/tabs/tabs.actions';
import {CommonService} from '../../services/common.service';
import {OptionMonitorService} from 'app/option-monitor/option-monitor.service';
import {FavouritesService} from 'app/favourites/services/favourites.service';
import {getAllFutures} from '@iv/store/futures/futures.reducer';
import {getPermissions} from '@iv/store/permissions/permissions.reducer';
import {AppStateService} from '../../services/app-state.service';
import {StatisticService} from '@iv/shared/statistic.service';
import {IUserSubscription} from 'app/core/interfaces/user-settings.interface';

@Component({
  selector: 'iv-live-main-tab-panel',
  templateUrl: './main-tab-panel.component.html',
  styleUrls: ['./main-tab-panel.component.scss']
})

export class MainTabPanelComponent implements OnInit, OnDestroy {

  constructor(
    private commonService: CommonService,
    private optionMonitorService: OptionMonitorService,
    private favoritesService: FavouritesService,
    @Inject(AppStore) private store: Redux.Store<AppState>,
    private appStateService: AppStateService,
    private statisticService: StatisticService
  ) {
    this.unsubscribeStore = store.subscribe(() => this.updateState());
    this.updateState();
  }

  public instruments: Future[] | Instrument[];
  public tabs: Tab[];
  public currentTab: Tab;
  public openNewTabPopupVisible = false;
  public advancedSearchVisible = false;
  public services = [];
  public openNewTabPopupPosition: any = {my: 'right bottom', at: 'top', of: '#addNewTabButton'};
  public permissions = getPermissions(this.store.getState());
  public haveFuturesAccess = this.appStateService.haveFuturesAccess;
  public haveStocksAccess = this.appStateService.haveStocksAccess;

  private unsubscribeStore;

  private static _getUid() {
    return '_' + Math.random().toString(36).substr(2, 9);
  }

  // TODO: instrument тут лежит две сущности, instrument и future
  private static _createInstrumentTab(instrument, type: string): any {
    const newTab = {
      id: MainTabPanelComponent._getUid(),
      type: type,
      active: true,
      component: type === 'instrument' ? 'OptionChainComponent' : 'FutureOptionChainComponent',
      name: instrument.symbol,
      description: type === 'instrument' ? instrument.company : instrument.name,
      data: {
        [type === 'instrument' ? 'instrument' : 'future']: instrument,
        chartType: 2,
        serviceTabs: [
          {
            active: true,
            id: 1,
            component: 'OptionMonitorComponent',
            name: type === 'instrument' ? 'Options Chain' : 'Future Options Chain',
            filters: {
              expirations: {
                'id': 'ALL',
                'value': 'All',
                'type': 'type'
              },
              strikesN: '5',
              type: {'name': 'Call&Put', 'value': ''}
            }
          }
        ]
      }
    };
    return Promise.resolve(newTab);
  }

  private static _createServiceTab(): any {
    return Promise.resolve({
      id: MainTabPanelComponent._getUid(),
      type: 'service',
      active: false,
      component: 'FavouritesComponent',
      name: 'Stock Monitor',
      description: 'Service',
      data: {
        selectedGroupId: 27,
        sorting: {
          'symbol': 'asc'
        }
      }
    });
  }

  private static _createFuturesMonitor(): Promise<any> {
    return Promise.resolve({
      id: MainTabPanelComponent._getUid(),
      type: 'futuresMonitor',
      active: true,
      component: 'FuturesMonitorComponent',
      name: 'Futures Monitor',
      description: 'FuturesMonitorComponent',
      data: {
        selectedGroupId: 15,
        sorting: {
          'symbol': 'asc'
        }
      }
    });
  }

  private static createChartServiceTab(): Promise<any> {
    return Promise.resolve({
      id: MainTabPanelComponent._getUid(),
      type: 'chartService',
      active: false,
      component: 'ChartsDashboardComponent',
      name: 'Charts',
      description: '',
      data: {}
    });
  }

  public ngOnInit(): void {
    if (this.haveStocksAccess) {
      this.services.push({
        serviceId: 1,
        name: 'Stock Monitor',
        type: 'service'
      });
    }
    if (this.haveFuturesAccess) {
      this.services.push({
        serviceId: 2,
        name: 'Futures Monitor',
        type: 'futuresMonitor'
      });
    }
    if (this.commonService.userSubscriptions
      .find((sub: IUserSubscription) => (sub.serviceId === 1007 || sub.serviceId === 1008) && sub.subscriptionCancelTypeId === 1)) {
      this.services.push({
        serviceId: 3,
        name: 'Charts',
        type: 'chartService'
      });
    }
  }

  public ngOnDestroy(): void {
    if (typeof this.unsubscribeStore === 'function') {
      this.unsubscribeStore();
    }
  }

  updateState() {
    const state = this.store.getState();
    this.currentTab = getCurrentTab(state);
    this.tabs = getTabs(state);

    if (this.haveStocksAccess) {
      this.instruments = [...getAllInstruments(state)];
    }

    if (this.haveFuturesAccess) {
      this.instruments = [...getAllFutures(state)];
    }

    if (this.haveFuturesAccess && this.haveStocksAccess) {
      this.instruments = [...getAllInstruments(state), ...getAllFutures(state)];
    }
  }

  onTabSelect(index) {
    const tab = this.getTabByIndex(index);
    this.sendStatistic(tab);

    this._hideChart(true);
    this.store.dispatch(TabsActions.selectTab(tab));
    this._hideChart(false);
  }

  sendStatistic(tab: Tab): void {
    if (tab.type === TabTypeEnum.OPTION_CHAIN) {
      this.statisticService.incrementViewTab(`Option chain ${tab.name}`);
      return;
    }

    if (tab.type === TabTypeEnum.FUTURES_OPTION_CHAIN) {
      this.statisticService.incrementViewTab(`Futures option chain ${tab.name}`);
      return;
    }
  }

  onTabClose(index) {
    const currentTab = this.currentTab;
    const tabToClose = this.getTabByIndex(index);
    if (currentTab.id === tabToClose.id) {
      this._hideChart(true);
    }
    this.store.dispatch(TabsActions.deleteTab(tabToClose));
  }

  onOpenNewTabClick() {
    this._changeNewTabPopupPosition();
    this.openNewTabPopupVisible = true;
  }

  _changeNewTabPopupPosition() {
    if (window.innerWidth - document.getElementsByClassName('new-tab-button')[0].getBoundingClientRect().left < 275) {
      this.openNewTabPopupPosition = {my: 'right top', at: 'bottom', of: '#addNewTabButton'};
    } else {
      this.openNewTabPopupPosition = {my: 'left top', at: 'bottom', of: '#addNewTabButton'};
    }
  }

  onSearchButtonClick() {
    this._showPopup('advancedSearchVisible', true);
  }

  onPopupCancel(popupVisible) {
    this[popupVisible] = false;
  }

  onAddSymbols(event) {
    this.openNewTab(event, TabTypeEnum.OPTION_CHAIN);
    this._showPopup('advancedSearchVisible', false);
    this._showPopup('openNewTabPopupVisible', false);
  }

  openNewTab(stockId?: number, type?: TabTypeEnum): void {
    this._hideChart(true);
    this.createTab(stockId, type)
      .then((tab: Tab) => {
        this.statisticService.incrementViewTab(tab.name);
        this.store.dispatch(TabsActions.addTab(tab));
        this.store.dispatch(TabsActions.selectTab(tab));
      });
  }

  createTab(stockId?: number, type?: string): Promise<Tab> {
    return new Promise((resolve) => {
      if (this.appStateService.haveStocksAccess) {
        if (type === 'instrument') resolve(MainTabPanelComponent._createInstrumentTab(this.getInstrumentById(stockId, type), type));
        if (type === 'service') resolve(MainTabPanelComponent._createServiceTab());
        if (
          type === 'chartService' &&
          this.commonService.userSubscriptions
            .find((sub: IUserSubscription) => (sub.serviceId === 1007 || sub.serviceId === 1008) && sub.subscriptionCancelTypeId === 1)
        ) {
          resolve(MainTabPanelComponent.createChartServiceTab());
        }
      }

      if (this.appStateService.haveFuturesAccess) {
        if (type === 'future') resolve(MainTabPanelComponent._createInstrumentTab(this.getInstrumentById(stockId, type), type));
        if (type === 'futuresMonitor') resolve(MainTabPanelComponent._createFuturesMonitor());
      }
    });
  }

  selectItemHandler(event: any): void {
    if (event.serviceId) {
      this.openNewTab(event.stockId, this.services.find(service => service.serviceId === event.serviceId).type);
    } else if (event.underlyingId) {
      this.openNewTab(event.underlyingId, TabTypeEnum.FUTURES_OPTION_CHAIN);
    } else if (event.stockId) {
      this.openNewTab(event.stockId, TabTypeEnum.OPTION_CHAIN);
    }
    this.openNewTabPopupVisible = false;
  }

  onOrderChange(tabs: any) {
    this.tabs = tabs;
    this.store.dispatch(TabsActions.loadTabs(this.tabs));
  }

  private getTabByIndex(index) {
    return this.tabs[index];
  }

  private _hideChart(hide) {
    this.commonService.hideChart(hide);
  }

  private _showPopup(popupName, show) {
    this[popupName] = show;
  }

  private getInstrumentById(id: number, type: string): Instrument | Future {
    if (this.instruments.length > 0) {
      return this.instruments.find((instrument: Future) => {
        return type === 'instrument' ? instrument.stockId === id : instrument.underlyingId === id;
      });
    } else {
      return null;
    }
  }
}
